/**
 * Pure helpers извлечённые из ChatPanel.jsx (1371 LOC) — first iteration
 * подготовки к split god-component'а. Все функции тут — pure (без DOM,
 * без api). Тестируемы через vitest без рендера React-дерева.
 *
 * - escapeMentionName / sanitizeMessageHtml / MENTION_RE — рендер mention-токенов
 * - isImageMessage — определение картиночного сообщения по file_url/file_name
 * - readReceiptsTitle / formatReadAt — текст для tooltip'а «Прочитали»
 * - formatTime / formatDate — локализация дат для Slack-подобного списка
 * - getInitials — фолбэк-аватар из имени
 * - QUICK_EMOJIS / EMOJI_CATEGORIES / EMOJI_RECENT_KEY — emoji-picker константы
 */

import { createMarkdown } from './markdown'
import DOMPurify from 'dompurify'

// ── Mention rendering ────────────────────────────────────────────
// Keep in sync with MENTION_RE в MentionTextarea.jsx.
export const MENTION_RE = /@\[([^\]]+)\]\(user:(?:[a-fA-F0-9-]{36}|[a-fA-F0-9]{32})\)/g

// MarkdownIt instance — module-level, чтобы не пересоздавать на каждый рендер.
const chatMd = createMarkdown({ html: false, linkify: true, breaks: true })

export function escapeMentionName(s) {
    return String(s).replace(/[&<>"']/g, c => (
        c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'
    ))
}

/**
 * Markdown rendering + DOMPurify sanitization + mention-token rewrite.
 * Mentions @[name](user:UUID) сначала заменяем на private-use Unicode
 * placeholders, чтобы Markdown их не съел, потом возвращаем span'ом.
 */
export function sanitizeMessageHtml(text) {
    const names = []
    const withTokens = String(text || '').replace(MENTION_RE, (_m, name) => {
        const idx = names.length
        names.push(name)
        return `${idx}`
    })
    const raw = chatMd.render(withTokens)
    const sanitized = DOMPurify.sanitize(raw, {
        ALLOWED_TAGS: [
            'p', 'br', 'strong', 'em', 's', 'code', 'pre', 'blockquote',
            'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'hr', 'span',
        ],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
        FORBID_ATTR: ['style', 'onerror', 'onclick'],
    })
    return sanitized.replace(/(\d+)/g, (_m, i) => {
        const name = names[Number(i)] ?? '?'
        return `<span class="chat-mention">@${escapeMentionName(name)}</span>`
    })
}

// ── Message type detection ───────────────────────────────────────
export function isImageMessage(msg) {
    if (!msg?.file_url) return false
    if (msg.message_type === 'image') return true
    const n = (msg.file_name || '').toLowerCase()
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(n)
}

// ── Read receipts ────────────────────────────────────────────────
export function readReceiptsTitle(msg) {
    if (!msg?.read_receipts?.length) return 'Пока не прочитано'
    const names = msg.read_receipts
        .map(r => r.full_name || r.user_id)
        .filter(Boolean)
        .slice(0, 5)
    const suffix = msg.read_receipts.length > 5 ? ` +${msg.read_receipts.length - 5}` : ''
    return `Прочитали: ${names.join(', ')}${suffix}`
}

export function formatReadAt(isoStr) {
    if (!isoStr) return 'время неизвестно'
    const d = new Date(isoStr)
    if (Number.isNaN(d.getTime())) return 'время неизвестно'
    return d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })
}

// ── Date/time formatting ─────────────────────────────────────────
export function formatTime(isoStr) {
    if (!isoStr) return ''
    const d = new Date(isoStr)
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
}

export function formatDate(isoStr) {
    if (!isoStr) return ''
    const d = new Date(isoStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (d.toDateString() === today.toDateString()) return 'Сегодня'
    if (d.toDateString() === yesterday.toDateString()) return 'Вчера'
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })
}

// ── Misc ─────────────────────────────────────────────────────────
export function getInitials(name) {
    if (!name) return '?'
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
}

// ── Emoji picker constants ───────────────────────────────────────
export const QUICK_EMOJIS = ['👍', '❤️', '😊', '✓', '★', '👏', '✕', '❓']
export const EMOJI_RECENT_KEY = 'chat:emoji:recent:v1'
export const EMOJI_CATEGORIES = {
    recent: { label: 'Недавние', items: [] },
    people: { label: 'Люди', items: ['😀', '😁', '😂', '😊', '😍', '😘', '😎', '🤔', '😢', '😭', '😡', '🥳'] },
    gesture: { label: 'Жесты', items: ['👍', '👎', '👏', '🙌', '🙏', '👌', '✌️', '⊕', '💪', '🤟', '👀', '✓'] },
    work: { label: 'Работа', items: ['▣', '⚐', '📎', '✎', '⏰', '▲', '▼', '◉', '▦', '⚠', '❗', '❓'] },
    nature: { label: 'Природа', items: ['★', '☀️', '🌈', '🌿', '🍀', '🌊', '❄️', '🌸', '🍎', '☕', '🍕', '●'] },
}
